import { apiClient } from "./client";

type WSMessageType =
  | "connected"
  | "room:joined"
  | "room:left"
  | "room:updated"
  | "room:started"
  | "room:finished"
  | "player:joined"
  | "player:left"
  | "player:connected"
  | "player:disconnected"
  | "game:started"
  | "game:turn_changed"
  | "game:rolled"
  | "game:announced"
  | "game:scored"
  | "game:finished"
  | "error";

interface WSMessage<T = unknown> {
  type: WSMessageType;
  payload: T;
  timestamp: number;
}

type MessageHandler = (message: WSMessage) => void;

class WebSocketClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<WSMessageType | "*", Set<MessageHandler>>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private pendingMessages: string[] = [];

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const token = apiClient.getToken();
      if (!token) {
        reject(new Error("Not authenticated"));
        return;
      }

      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        // Wait for existing connection attempt
        const checkConnection = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            clearInterval(checkConnection);
            resolve();
          }
        }, 100);
        return;
      }

      this.isConnecting = true;
      const wsUrl = `${import.meta.env.VITE_WS_URL ?? "ws://localhost:4000"}?token=${token}`;

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log("WebSocket connected");
          this.isConnecting = false;
          this.reconnectAttempts = 0;

          // Send any pending messages
          for (const msg of this.pendingMessages) {
            this.ws?.send(msg);
          }
          this.pendingMessages = [];

          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as WSMessage;
            this.handleMessage(message);
          } catch (error) {
            console.error("Failed to parse WebSocket message:", error);
          }
        };

        this.ws.onclose = (event) => {
          console.log("WebSocket closed:", event.code, event.reason);
          this.isConnecting = false;
          this.ws = null;

          // Attempt reconnect if not intentionally closed
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.attemptReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error("WebSocket error:", error);
          this.isConnecting = false;
          reject(error);
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private attemptReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    console.log(`Attempting reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect().catch((error) => {
        console.error("Reconnect failed:", error);
      });
    }, delay);
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close(1000, "User disconnected");
      this.ws = null;
    }
  }

  send<T>(type: string, payload: T): void {
    const message = JSON.stringify({ type, payload, timestamp: Date.now() });

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(message);
    } else {
      // Queue message to send when connected
      this.pendingMessages.push(message);
      this.connect().catch(console.error);
    }
  }

  on(type: WSMessageType | "*", handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(type)?.delete(handler);
    };
  }

  off(type: WSMessageType | "*", handler: MessageHandler): void {
    this.handlers.get(type)?.delete(handler);
  }

  private handleMessage(message: WSMessage): void {
    // Call specific handlers
    const specificHandlers = this.handlers.get(message.type);
    if (specificHandlers) {
      for (const handler of specificHandlers) {
        try {
          handler(message);
        } catch (error) {
          console.error(`Handler error for ${message.type}:`, error);
        }
      }
    }

    // Call wildcard handlers
    const wildcardHandlers = this.handlers.get("*");
    if (wildcardHandlers) {
      for (const handler of wildcardHandlers) {
        try {
          handler(message);
        } catch (error) {
          console.error("Wildcard handler error:", error);
        }
      }
    }
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export const wsClient = new WebSocketClient();
